// @ts-strict-ignore
import * as React from 'react';

import Modal from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Select from 'antd/lib/select';
import { BetaTester, IntegrationType, RunwayResponseType } from '../../types';

import Form from 'antd/lib/form';
import { AddIndividualTesterToBuildRequestReleaseStep, tryUpdateRelease } from '../../actions/release';
import { useDispatch, useSelector } from 'react-redux';
import { ReleaseUpdatable } from '../../actions/updatable';
import Link from 'antd/lib/typography/Link';
import { RootState } from '../../reducers/reducers';
import { selectBetaIntegrationLinkUrl } from '../../selectors/selectors';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';

interface BetaTestersDialogProps extends ReleaseComponentProps {
    open: boolean;
    onCancel: () => void;
    betaIntegrationType: IntegrationType;
    betaTesters: {
        selected: string[];
        all: BetaTester[];
    };
    onSaveSuccess: () => void;
    externalBuildId: string;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const BetaTestersDialog = (props: BetaTestersDialogProps): JSX.Element => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const isAddLoadingReleaseStep = useIsLoading(ReleaseUpdatable.AddIndividualTesterToBuild, props.releaseId);
    const isAddSuccessReleaseStep = useUpdated(ReleaseUpdatable.AddIndividualTesterToBuild, props.releaseId);
    const addErrorReleaseStep = useError(ReleaseUpdatable.AddIndividualTesterToBuild, props.releaseId);

    const getTesterIdByEmail = (testerEmail: string): string => {
        return props.betaTesters.all.find((betaTester) => betaTester.email === testerEmail).id;
    };

    const [
        addIndividualTesterToBuild,
        addIndividualTesterToBuildIsLoading,
        addIndividualTesterToBuildResponseType,
        addIndividualTesterToBuildError,
    ] = useCallUpdatable(StepRunUpdatable.AddIndividualTesterToBuild, props.stepRunComponentProps);

    const isAddLoading = props.stepRunComponentProps ? addIndividualTesterToBuildIsLoading : isAddLoadingReleaseStep;
    const isAddSuccess =
        props.stepRunComponentProps ?
            addIndividualTesterToBuildResponseType === RunwayResponseType.Success
        :   isAddSuccessReleaseStep;
    const addError = props.stepRunComponentProps ? addIndividualTesterToBuildError : addErrorReleaseStep;

    const betaLinkUrl = useSelector((state: RootState) =>
        selectBetaIntegrationLinkUrl(state, props.appId, props.betaIntegrationType),
    );

    const onCancel = () => {
        props.onCancel();
    };

    const onSubmit = (values) => {
        const request: AddIndividualTesterToBuildRequestReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            buildId: props.externalBuildId,
            releaseId: props.releaseId,
            testerId: getTesterIdByEmail(values.testerIds[0]),
        };

        if (props.stepRunComponentProps) {
            addIndividualTesterToBuild(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.AddIndividualTesterToBuild, request, props.releaseId));
        }
    };

    React.useEffect(() => {
        if (props.open) {
            form.setFieldsValue({ testerIds: props.betaTesters.selected });
        }
    }, [props.open]);

    React.useEffect(() => {
        if (isAddSuccess) {
            globalMessage.success('Added beta testers to build!');
            props.onSaveSuccess();
        }
    }, [isAddSuccess]);

    React.useEffect(() => {
        if (addError) {
            globalMessage.error(addError);
        }
    }, [addError]);

    return (
        <Modal
            open={props.open}
            title="Edit individual testers"
            onCancel={onCancel}
            onOk={() => form.submit()}
            okText="Save"
            okButtonProps={{ loading: isAddLoading }}
            focusTriggerAfterClose={false}
            className="modal-with-background"
        >
            <div>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Text type="secondary">
                        Go to{' '}
                        <Link href={betaLinkUrl} target="_blank" style={{ whiteSpace: 'nowrap' }}>
                            TestFlight
                        </Link>{' '}
                        to remove individual testers who currently have access.
                    </Text>
                    <Text type="secondary">
                        Click to see a menu of testers, or start typing to filter the list. You can include multiple
                        testers.
                    </Text>
                    <Form onFinish={onSubmit} form={form} layout="vertical" colon={false}>
                        <Form.Item name="testerIds" label={<Text strong>Individual Testers</Text>}>
                            <Select allowClear mode="multiple" placeholder="Select testers" style={{ width: '100%' }}>
                                {props.betaTesters.all?.map((tester) => (
                                    <Select.Option
                                        key={tester.email}
                                        value={tester.email}
                                        disabled={props.betaTesters.selected.includes(tester.email)}
                                    >
                                        {tester.email}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </Space>
            </div>
        </Modal>
    );
};

export default BetaTestersDialog;
